import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Center, Stack, Text } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import PinVerificationModal from "../../Account/PinVerificationModal";
import {
  isPinUnlockSessionActive,
  getRemainingPinUnlockMs,
} from "../../../utils/pinUnlockSession";

// Wraps a pay surface. Children render only while a PIN unlock is live; the
// rest of the time the reader gets a lock screen and the PIN prompt.
//
// This is a convenience, not the enforcement — the pay endpoints themselves
// require the unlock grant, so nothing here is what keeps the numbers safe.
// `onUnlock` fires when a PIN is accepted, so the page can fetch.
function PinGate({
  children,
  title = "Pay details are protected",
  description = "Enter your 4-digit security PIN to view pay details.",
  onUnlock,
}) {
  const [unlocked, setUnlocked] = useState(() => isPinUnlockSessionActive());
  const [promptOpen, setPromptOpen] = useState(false);

  // The unlock is time-boxed, so re-lock as soon as the window closes.
  useEffect(() => {
    if (!unlocked) return undefined;

    const interval = setInterval(() => {
      if (!isPinUnlockSessionActive()) setUnlocked(false);
    }, 1000);

    return () => clearInterval(interval);
  }, [unlocked]);

  const handleSuccess = useCallback(() => {
    setUnlocked(true);
    setPromptOpen(false);
    onUnlock?.();
  }, [onUnlock]);

  if (unlocked && getRemainingPinUnlockMs() > 0) return <>{children}</>;

  return (
    <>
      <Center py="xl">
        <Card withBorder radius="md" p="xl" maw={420}>
          <Stack align="center" gap="sm">
            <IconLock size={32} color="var(--mantine-color-gray-5)" />
            <Text fw={600}>{title}</Text>
            <Text size="sm" c="dimmed" ta="center">
              {description}
            </Text>
            <Button
              mt="sm"
              color="orange"
              leftSection={<IconLock size={16} />}
              onClick={() => setPromptOpen(true)}
            >
              Enter PIN
            </Button>
          </Stack>
        </Card>
      </Center>

      <PinVerificationModal
        isOpen={promptOpen}
        onClose={() => setPromptOpen(false)}
        onSuccess={handleSuccess}
      />
    </>
  );
}

export default PinGate;
